import { useEffect, useRef, useState } from "react";
import useHustleSlots from "@/lib/useHustleSlots";

/**
 * Renders a list of typed CMS page blocks with motion / media.
 * Supported kinds: text · fade · slide · parallax · marquee · carousel · gallery.
 * Falls back to plain text rendering for unknown kinds.
 *
 * When blocks contain Hustle shortcodes ([slot:...], [include:...]) they are
 * resolved live against the active business's slot + snippet pack.
 */
export default function PageBlockRenderer({ blocks = [] }) {
  const { resolve } = useHustleSlots();
  if (!Array.isArray(blocks) || blocks.length === 0) return null;
  const resolved = blocks.map((b) => ({
    ...b,
    heading: resolve(b.heading),
    content: resolve(b.content),
  }));
  return (
    <div data-testid="page-blocks" className="space-y-12 md:space-y-16">
      {resolved.map((b, i) => (
        <Block key={b.id || i} block={b} index={i} />
      ))}
    </div>
  );
}

function Block({ block }) {
  const kind = (block?.kind || "text").toLowerCase();
  if (kind === "fade") return <FadeBlock {...block} />;
  if (kind === "slide") return <SlideBlock {...block} />;
  if (kind === "parallax") return <ParallaxBlock {...block} />;
  if (kind === "marquee") return <MarqueeBlock {...block} />;
  if (kind === "carousel") return <CarouselBlock {...block} />;
  if (kind === "gallery") return <GalleryBlock {...block} />;
  if (kind === "faq") return <FaqBlock {...block} />;
  return <TextBlock {...block} />;
}

/* ---------------- shared helpers ---------------- */
function Paragraphs({ content }) {
  const paras = String(content || "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  return paras.map((p, i) => (
    <p
      key={i}
      className="mt-4 leading-relaxed text-[var(--biz-ink,#3a3c3a)]/85 whitespace-pre-line"
    >
      {p}
    </p>
  ));
}

function useInView(once = true) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            if (once) obs.disconnect();
          } else if (!once) {
            setShown(false);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -40px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [once]);
  return [ref, shown];
}

/* ---------------- block components ---------------- */
function TextBlock({ heading, content }) {
  return (
    <section data-testid="block-text" className="max-w-3xl">
      {heading && <h2 className="font-serif-display text-3xl md:text-4xl">{heading}</h2>}
      <Paragraphs content={content} />
    </section>
  );
}

/* FAQ block — heading = question, content = answer.
   Rendered as a <details>/<summary> accordion. Its data also flows
   into the JSON-LD FAQPage schema emitted by SeoHead. */
function FaqBlock({ heading, content, settings = {} }) {
  const question = settings.question || heading || "";
  const answer = settings.answer || content || "";
  if (!question && !answer) return null;
  return (
    <section data-testid="block-faq" className="max-w-3xl">
      <details className="group border-b border-[var(--biz-ink,#121614)]/12 py-4">
        <summary
          data-testid="faq-question"
          className="cursor-pointer list-none font-serif-display text-lg md:text-xl text-[var(--biz-ink,#121614)] flex items-center justify-between gap-4"
        >
          <span>{question}</span>
          <span
            aria-hidden
            className="inline-block h-6 w-6 rounded-full border border-[var(--biz-ink,#121614)]/20 grid place-items-center text-sm transition-transform group-open:rotate-45"
          >
            +
          </span>
        </summary>
        <div
          data-testid="faq-answer"
          className="mt-3 leading-relaxed text-[var(--biz-ink,#3a3c3a)]/85 whitespace-pre-line"
        >
          {answer}
        </div>
      </details>
    </section>
  );
}

function FadeBlock({ heading, content }) {
  const [ref, shown] = useInView();
  return (
    <section
      ref={ref}
      data-testid="block-fade"
      className={`max-w-3xl transition-all duration-700 ease-out ${
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
    >
      {heading && <h2 className="font-serif-display text-3xl md:text-4xl">{heading}</h2>}
      <Paragraphs content={content} />
    </section>
  );
}

function SlideBlock({ heading, content, settings = {} }) {
  const [ref, shown] = useInView();
  const dir = (settings.direction || "left").toLowerCase();
  const off = dir === "right" ? "translate-x-10" : "-translate-x-10";
  return (
    <section
      ref={ref}
      data-testid="block-slide"
      className={`max-w-3xl transition-all duration-700 ease-out ${
        shown ? "opacity-100 translate-x-0" : `opacity-0 ${off}`
      }`}
    >
      {heading && <h2 className="font-serif-display text-3xl md:text-4xl">{heading}</h2>}
      <Paragraphs content={content} />
    </section>
  );
}

function ParallaxBlock({ heading, content, images = [], settings = {} }) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  const bg = images?.[0] || "";
  const height = Number(settings.height) || 420;
  useEffect(() => {
    if (!bg) return undefined;
    const onScroll = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const progress = (vh - rect.top) / (vh + rect.height);
      const clamped = Math.max(0, Math.min(1, progress));
      // Move bg up to 60px relative to scroll for a clear parallax effect.
      setOffset((clamped - 0.5) * 60);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [bg]);

  return (
    <section
      ref={ref}
      data-testid="block-parallax"
      className="relative overflow-hidden rounded-3xl"
      style={{ minHeight: `${height}px` }}
    >
      {bg ? (
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${bg})`,
            backgroundSize: "cover",
            backgroundPosition: `center calc(50% + ${offset}px)`,
            backgroundRepeat: "no-repeat",
            transform: "scale(1.05)",
          }}
        />
      ) : (
        <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-[var(--biz-primary,#C05746)]/30 to-[var(--biz-accent,#5A7247)]/30" />
      )}
      <div className="absolute inset-0 bg-black/35" aria-hidden />
      <div className="relative max-w-3xl px-6 md:px-10 py-16 md:py-24 text-white">
        {heading && <h2 className="font-serif-display text-3xl md:text-5xl">{heading}</h2>}
        <div className="text-white/90"><Paragraphs content={content} /></div>
      </div>
    </section>
  );
}

function MarqueeBlock({ content, settings = {} }) {
  const speed = Math.max(10, Math.min(120, Number(settings.speed) || 40));
  const text = String(content || "").trim() || "•";
  return (
    <section
      data-testid="block-marquee"
      className="border-y border-[var(--biz-ink,#121614)]/10 py-4 overflow-hidden bg-[var(--biz-surface,#FDFBF7)]"
    >
      <div
        className="marquee-track whitespace-nowrap font-serif-display text-2xl md:text-3xl text-[var(--biz-primary,#C05746)]"
        style={{ animation: `pbr-marquee ${speed}s linear infinite` }}
      >
        <span className="px-8">{text}</span>
        <span className="px-8" aria-hidden>{text}</span>
        <span className="px-8" aria-hidden>{text}</span>
      </div>
    </section>
  );
}

function CarouselBlock({ heading, content, images = [], settings = {} }) {
  const interval = Math.max(1500, Number(settings.interval_ms) || 4000);
  const list = (images || []).filter(Boolean);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (list.length < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), interval);
    return () => clearInterval(t);
  }, [list.length, interval]);

  if (list.length === 0) {
    return (
      <section data-testid="block-carousel" className="max-w-3xl">
        {heading && <h2 className="font-serif-display text-3xl md:text-4xl">{heading}</h2>}
        <div className="mt-4 aspect-[16/9] rounded-2xl border-2 border-dashed border-[var(--biz-ink,#121614)]/15 grid place-items-center text-sm text-[var(--biz-muted,#5C625E)]">
          Add carousel images in the editor.
        </div>
        <Paragraphs content={content} />
      </section>
    );
  }

  return (
    <section data-testid="block-carousel">
      {heading && <h2 className="font-serif-display text-3xl md:text-4xl max-w-3xl">{heading}</h2>}
      <div className="mt-4 relative aspect-[16/9] rounded-3xl overflow-hidden bg-black/5">
        {list.map((src, i) => (
          <img
            key={src + i}
            src={src}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
              i === idx ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {list.map((_, i) => (
            <button
              key={i}
              data-testid={`block-carousel-dot-${i}`}
              onClick={() => setIdx(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "w-6 bg-white" : "w-1.5 bg-white/60 hover:bg-white"
              }`}
            />
          ))}
        </div>
      </div>
      <div className="max-w-3xl"><Paragraphs content={content} /></div>
    </section>
  );
}

function GalleryBlock({ heading, content, images = [] }) {
  const list = (images || []).filter(Boolean);
  return (
    <section data-testid="block-gallery">
      {heading && <h2 className="font-serif-display text-3xl md:text-4xl max-w-3xl">{heading}</h2>}
      {list.length === 0 ? (
        <div className="mt-4 aspect-[16/9] rounded-2xl border-2 border-dashed border-[var(--biz-ink,#121614)]/15 grid place-items-center text-sm text-[var(--biz-muted,#5C625E)] max-w-3xl">
          Add gallery images in the editor.
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          {list.map((src, i) => (
            <div
              key={src + i}
              className={`rounded-2xl overflow-hidden ${
                i % 5 === 0 ? "md:col-span-2 md:row-span-2 aspect-square" : "aspect-square"
              }`}
            >
              <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
          ))}
        </div>
      )}
      <div className="max-w-3xl"><Paragraphs content={content} /></div>
    </section>
  );
}
